import type {
	GPGamePlayerResponse,
	MergedGpEventResponse,
} from "../../../../types";
import { REPORT_EVENT_GROUPS, TYPE_CHIP_COLORS } from "./reportError.constants";
import type { ReportEventOption, WrongEventOption } from "./reportError.types";

const REPORT_EVENT_OPTIONS = REPORT_EVENT_GROUPS.flatMap(
	(group) => group.options,
);

export const normalizeEventType = (value: string) =>
	value.replace(/[\s_-]/g, "").toLowerCase();

export const formatEventTypeLabel = (value: string) =>
	value.replaceAll("_", " ");

export const getPrimaryEventType = (event: MergedGpEventResponse | null) =>
	event?.types[0] ?? "";

export const getPlayerForEvent = (
	event: MergedGpEventResponse | null,
	players?: GPGamePlayerResponse[],
) => {
	if (!event || players === undefined || players.length === 0) {
		return null;
	}

	return (
		players.find((player) => player.id === event.gamePlayerId) ??
		players.find(
			(player) =>
				player.teamId === event.gameTeamId &&
				player.numberSign === event.backNumber,
		) ??
		null
	);
};

export const getReportEventOption = (
	value?: string | null,
): ReportEventOption | null => {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	const normalizedValue = normalizeEventType(value);

	return (
		REPORT_EVENT_OPTIONS.find(
			(option) =>
				normalizeEventType(option.value) === normalizedValue ||
				option.aliases?.some(
					(alias) => normalizeEventType(alias) === normalizedValue,
				),
		) ?? null
	);
};

export const buildWrongEventOptions = (
	selectedEvent: MergedGpEventResponse | null,
	selectedEvents: MergedGpEventResponse[],
): WrongEventOption[] => {
	if (selectedEvents.length > 1) {
		return selectedEvents.flatMap((event) =>
			event.types.flatMap((eventType, typeIndex) => {
				if (!eventType) {
					return [];
				}

				const key =
					event.types.length === 1
						? `${event.id}`
						: `${event.id}-${eventType}-${typeIndex}`;

				return [
					{
						key,
						value: key,
						label: formatEventTypeLabel(eventType),
						eventId: event.id,
						eventType,
						chipColorClassName:
							TYPE_CHIP_COLORS[typeIndex % TYPE_CHIP_COLORS.length],
					},
				];
			}),
		);
	}

	if (!selectedEvent) {
		return [];
	}

	return selectedEvent.types.map((eventType, index) => ({
		key: `${selectedEvent.id}-${eventType}-${index}`,
		value: `${selectedEvent.id}-${eventType}-${index}`,
		label: formatEventTypeLabel(eventType),
		eventId: selectedEvent.id,
		eventType,
		chipColorClassName: TYPE_CHIP_COLORS[index % TYPE_CHIP_COLORS.length],
	}));
};

export const getDefaultWrongEventKey = (
	wrongEventOptions: WrongEventOption[],
	selectedEvent: MergedGpEventResponse | null,
): string => {
	if (!selectedEvent) {
		return wrongEventOptions[0]?.key ?? "";
	}

	const selectedEventType = getPrimaryEventType(selectedEvent);
	const matchingOption = wrongEventOptions.find(
		(option) =>
			option.eventId === selectedEvent.id &&
			option.eventType === selectedEventType,
	);

	return matchingOption?.key ?? wrongEventOptions[0]?.key ?? "";
};

export const getWrongEventOption = (
	wrongEventOptions: WrongEventOption[],
	wrongEventKey: string,
) => wrongEventOptions.find((option) => option.key === wrongEventKey) ?? null;

export const getActiveEventFromWrongEvent = (
	selectedEvent: MergedGpEventResponse | null,
	selectedEvents: MergedGpEventResponse[],
	wrongEventOption: WrongEventOption | null,
) => {
	if (!selectedEvent) {
		return null;
	}

	if (!wrongEventOption) {
		return selectedEvent;
	}

	return (
		selectedEvents.find((event) => event.id === wrongEventOption.eventId) ??
		selectedEvent
	);
};
